import { renderElement } from "../../utils/use-render.js";
import { useMenuOptionState } from "../menu/context.js";
import { MenuRadioGroup } from "../menu/radio-group.js";
import { useCaptionsOptions } from "./use-captions-options.js";
import { CaptionsRadioGroupDataAttrs } from "@videojs/core";
import { getStateDataAttrs } from "@videojs/core/dom";
import { Fragment, createContext, forwardRef, useContext } from "react";
import { jsx } from "react/jsx-runtime";
import { isFunction } from "@videojs/utils/predicate";
//#region src/ui/captions-radio-group/component.tsx
const CaptionsRadioGroupContext = createContext(void 0);
/** Owns captions option state and shares it with an enclosing menu. Does not render a DOM element. */
function CaptionsRadioGroupRoot({ children, ...props }) {
	const captions = useCaptionsOptions(props);
	useMenuOptionState(toMenuOptionState(captions));
	return /* @__PURE__ */ jsx(CaptionsRadioGroupContext.Provider, {
		value: captions,
		children
	});
}
/** Displays the selected captions label. */
const CaptionsRadioGroupValue = forwardRef(function CaptionsRadioGroupValue({ render, className, style, ...elementProps }, forwardedRef) {
	const captions = useCaptionsRadioGroupContext();
	if (!captions) return null;
	return renderElement("span", {
		render,
		className,
		style
	}, {
		state: captions,
		ref: forwardedRef,
		props: [elementProps, { children: captions.selectedLabel }]
	});
});
/** Renders menu radio items for the player's captions and subtitles tracks. */
const CaptionsRadioGroupOptions = forwardRef(function CaptionsRadioGroupOptions(componentProps, forwardedRef) {
	const { renderItem, render, className, style, "aria-label": ariaLabelProp, "aria-labelledby": ariaLabelledBy, ...elementProps } = componentProps;
	const captions = useCaptionsRadioGroupContext();
	if (!captions) return null;
	const { state, value, options, setValue } = captions;
	const ariaLabel = ariaLabelProp ?? (ariaLabelledBy === void 0 ? captions.label : void 0);
	return /* @__PURE__ */ jsx(MenuRadioGroup, {
		...getStateDataAttrs(state, CaptionsRadioGroupDataAttrs),
		...elementProps,
		ref: forwardedRef,
		className: isFunction(className) ? className(state) : className,
		style: isFunction(style) ? style(state) : style,
		render: isFunction(render) ? (props) => render(props, state) : render,
		value,
		onValueChange: setValue,
		"aria-label": ariaLabel,
		"aria-labelledby": ariaLabelledBy,
		"aria-disabled": state.disabled || void 0,
		hidden: state.hidden,
		children: options.map((option) => {
			const itemState = {
				...option,
				checked: option.value === value
			};
			return /* @__PURE__ */ jsx(Fragment, { children: renderItem({
				value: option.value,
				disabled: option.disabled,
				"data-track": option.value,
				children: option.label
			}, itemState) }, option.value);
		})
	});
});
/** @internal Compatibility adapter for the existing preset sources. */
const CaptionsRadioGroupLegacy = forwardRef(function CaptionsRadioGroupLegacy({ label, formatTrack, disabled, ...props }, forwardedRef) {
	return /* @__PURE__ */ jsx(CaptionsRadioGroupRoot, {
		label,
		formatTrack,
		disabled,
		children: /* @__PURE__ */ jsx(CaptionsRadioGroupOptions, {
			...props,
			ref: forwardedRef
		})
	});
});
function useCaptionsRadioGroupContext() {
	const captions = useContext(CaptionsRadioGroupContext);
	if (captions === void 0) throw new Error("CaptionsRadioGroup parts must be used within CaptionsRadioGroup.Root");
	return captions;
}
function toMenuOptionState(captions) {
	return {
		value: captions?.selectedLabel ?? "",
		disabled: captions?.disabled ?? true,
		hidden: captions?.hidden ?? true,
		availability: captions?.state.availability ?? "unsupported"
	};
}
//#endregion
export { CaptionsRadioGroupLegacy, CaptionsRadioGroupOptions, CaptionsRadioGroupRoot, CaptionsRadioGroupValue };

//# sourceMappingURL=component.js.map